import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Action, Button, Icon, ScrollView, Switch, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { DEFAULT_LOCALE, BENEFIT_MIN_WIDTH } from './Banner.constants';
import { L10N } from './Banner.l10n';
import * as style from './Banner.module.css';
import Skeleton from './Banner.skeleton';
import { EVENT, getNormalizedLocale, ICON, METRICS_EVENTS } from '../helpers';

const Banner = ({ clubConditions, ...others }) => {
  const { isDesktop, isMobile } = useDevice();
  const { locale: stateLocale, translate } = useLocale();
  const {
    value: { banner: storeBanner, club = {}, finder: { place } = {}, session, skeleton = false },
  } = useStore();

  const [banner, setBanner] = useState(storeBanner);
  const [clubActive, setClubActive] = useState(club.active);
  const [clubDiscount, setClubDiscount] = useState(club.discount);
  const [expanded, setExpanded] = useState(false);
  const [scrollIndex, setScrollIndex] = useState(0);

  const itemRef = useRef();
  const loyaltyMetricsSent = useRef(false);

  useEffect(() => {
    const callback = (nextClubDiscount) =>
      setClubDiscount(typeof nextClubDiscount !== 'object' ? nextClubDiscount : undefined);

    Event.subscribe(EVENT.RATES_FILTER_CLUB, callback);

    return () => Event.unsubscribe(EVENT.RATES_FILTER_CLUB, callback);
  }, []);

  useEffect(() => {
    if (!place) return;

    const { id: [hotelId] = [], isHotel } = place || {};
    const { variants = {} } = storeBanner || {};
    const { variants: clubVariants = [] } = club;
    if (hotelId && (Object.keys(variants).length || clubVariants.length)) {
      const nextBanner = variants[isHotel && hotelId] || storeBanner || {};
      const clubVariant = isHotel
        ? clubVariants?.find(({ id = '' } = {}) => id.toString() === hotelId.toString())
        : club;

      nextBanner && setBanner({ ...banner, ...nextBanner });
      setClubActive(clubVariant?.active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  const handlePressBenefit = (index) => {
    setScrollIndex(index);
    isClub &&
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.RATES_LOYALTY_INTERACTION,
        action: 'loyalty_banner_interaction',
        loyalty_interaction_subtype: 'slider_move',
      });
  };

  const handlePressFilter = (value) => {
    Event.publish(EVENT.RATES_FILTER_CLUB, value);
    isClub &&
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.RATES_LOYALTY_INTERACTION,
        action: 'loyalty_banner_interaction',
        loyalty_interaction_subtype: 'discount_toggle',
        loyalty_discount_active: value,
      });
  };

  const Filter = () => (
    <View row className={style.filter}>
      <Text action medium>
        {translate(L10N.LABEL_ONLY_MEMBERS)}
      </Text>
      <Switch
        checked={clubDiscount}
        name={`switch`}
        onChange={handlePressFilter}
        aria-label={translate(L10N.LABEL_ONLY_MEMBERS)}
      />
    </View>
  );

  const { benefits = [], description = {}, isClub, title, url } = banner || {};
  const buttonProps = { secondary: true, squared: true, rounded: true };
  const lastBenefit = scrollIndex === benefits.length - 4;
  const locale = getNormalizedLocale(stateLocale);
  const shouldRender = (clubActive && !session) || (!isClub && banner);

  useEffect(() => {
    if (skeleton || loyaltyMetricsSent.current) return;

    const timeout = setTimeout(() => {
      loyaltyMetricsSent.current = true;
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.RATES_LOYALTY_READY,
        isClub,
        clubActive,
        shouldRender: !!shouldRender,
        hasSession: !!session,
        hasTitle: !!title,
        hasBanner: !!banner && Object.keys(banner).length > 0,
      });
    }, 0);

    return () => clearTimeout(timeout);
  }, [clubActive, banner, isClub, shouldRender, title, session, skeleton]);

  return shouldRender && skeleton ? (
    <Skeleton />
  ) : shouldRender && title ? (
    <View testId={others.testId} className={[style.container, others.className]}>
      <View row className={style.highlight}>
        <View>
          <Text bold brand headline level={isDesktop ? 2 : 3} className={style.title}>
            {title[locale] || title[DEFAULT_LOCALE]}
          </Text>
          <Text action={isDesktop} small={!isDesktop}>
            {description[locale] || description[DEFAULT_LOCALE]}
            {url && (
              <>
                {' '}
                <Action bold href={url} target="_blank" underline className={style.textInline}>
                  {translate(L10N.ACTION_MORE_INFO)}
                </Action>
              </>
            )}
          </Text>
          {clubConditions && (
            <Text action={isDesktop} small={!isDesktop}>
              {translate(L10N.LABEL_CLUB_CONDITIONS, {
                conditions: (
                  <Action href={clubConditions} target="_blank" underline className={style.textInline}>
                    {translate(L10N.ACTION_CLUB_VIEW_CONDITIONS)}
                  </Action>
                ),
              })}
            </Text>
          )}
        </View>

        {isMobile ? (
          <Button small squared transparent onPress={handleExpanded}>
            <Icon value={expanded ? ICON.EXPAND_LESS : ICON.EXPAND_MORE} />
          </Button>
        ) : (
          clubDiscount !== undefined && <Filter />
        )}
      </View>

      {(!isMobile || expanded) && benefits.length > 0 && (
        <View row wide>
          {!isMobile && scrollIndex !== 0 && (
            <Button {...buttonProps} onPress={() => handlePressBenefit(scrollIndex - 1)}>
              <Icon value={ICON.LEFT} />
            </Button>
          )}

          {React.createElement(
            isMobile ? View : ScrollView,
            {
              ...(!isMobile
                ? {
                    horizontal: !isMobile,
                    scrollTo: scrollIndex * (itemRef?.current?.offsetWidth || BENEFIT_MIN_WIDTH),
                  }
                : {}),
              className: style.list,
            },
            benefits.map(({ description = {}, icon } = {}, index) => (
              <View ref={index === 0 ? itemRef : undefined} row key={index}>
                <Icon accent headline level={!isDesktop ? 3 : 2} value={ICON[icon] || ICON.AMENITY} />
                <Text action={isDesktop} small={!isDesktop}>
                  {description[locale] || description[DEFAULT_LOCALE]}
                </Text>
              </View>
            )),
          )}

          {!isMobile && benefits.length > 4 && (
            <Button {...buttonProps} onPress={() => handlePressBenefit(lastBenefit ? 0 : scrollIndex + 1)}>
              <Icon value={lastBenefit ? ICON.REPLAY : ICON.RIGHT} />
            </Button>
          )}
        </View>
      )}

      {isMobile && clubDiscount !== undefined && <Filter />}
    </View>
  ) : null;
};

Banner.displayName = 'Mirai:Core:Banner';

Banner.propTypes = {
  clubConditions: PropTypes.string,
};

export { Banner };
